import React, { useEffect, useState } from "react"
import Popup from "component/popup/popup"
import InputText from "component/inputText/inputText"
import Select from "component/select/select"
import Toggle from "component/toggle/Toggle"
import { agent } from "apiClient/api"
import { useSnackbar } from "context/snackbarContext"
import {
    buildUpdateUserPayload,
    CONFIGURATION_ROLE_OPTIONS,
    ConfigurationUser,
    ConfigurationUserRoleName,
    identifierError,
    nameError,
    sanitizePhone,
} from "./configurationUsers"
import styles from "./configuration.module.scss"

type EditUserPopupProps = {
    isOpen: boolean
    user: ConfigurationUser | null
    isSelf: boolean
    onClose: () => void
    onSuccess: () => void
}

const EditUserPopup: React.FC<EditUserPopupProps> = ({ isOpen, user, isSelf, onClose, onSuccess }) => {
    const { showSuccess, showError } = useSnackbar()
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [email, setEmail] = useState("")
    const [phone, setPhone] = useState("")
    const [roleName, setRoleName] = useState<ConfigurationUserRoleName>("User")
    const [flagActive, setFlagActive] = useState(true)
    const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; identifier?: string; role?: string }>({})
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        if (!isOpen || !user) return
        setFirstName(user.first_name)
        setLastName(user.last_name)
        setEmail(user.email)
        setPhone(user.phone)
        setRoleName(user.role_name)
        setFlagActive(user.flag_active)
        setErrors({})
    }, [isOpen, user])

    const handleSave = async () => {
        if (!user) return
        const nextErrors: typeof errors = {
            firstName: nameError(firstName, "First name"),
            identifier: identifierError(user.username_type, email, phone),
            role: roleName ? undefined : "Role is required",
        }
        setErrors(nextErrors)
        if (Object.values(nextErrors).some(Boolean)) return

        setIsSaving(true)
        try {
            const response = await agent.UpdateUserAPI.put(
                buildUpdateUserPayload(user, {
                    firstName,
                    lastName,
                    email,
                    phone,
                    roleName,
                    flagActive: isSelf ? user.flag_active : flagActive,
                }),
            )
            if (response?.status === true) {
                showSuccess("User updated")
                onSuccess()
                onClose()
            } else {
                showError(response?.error?.message || "Failed to update user")
            }
        } catch (err) {
            console.error("Update user error", err)
            showError("Failed to update user")
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Popup
            isOpen={isOpen}
            onClose={onClose}
            title="Edit User"
            size="medium"
            secondaryButton={{ label: "Cancel", onClick: onClose }}
            primaryButton={{ label: isSaving ? "Saving..." : "Save Changes", onClick: handleSave, disabled: isSaving }}
            showCloseButton={false}
        >
            <div className={styles.formStack}>
                <div className={styles.formRow}>
                    <InputText
                        label="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Enter first name"
                        error={errors.firstName}
                    />
                    <InputText
                        label="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Enter last name"
                        error={errors.lastName}
                    />
                </div>
                {user?.username_type === "phone" ? (
                    <InputText
                        label="Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                        placeholder="Enter 10 digit phone number"
                        type="tel"
                        maxLength={10}
                        error={errors.identifier}
                    />
                ) : (
                    <InputText
                        label="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter email"
                        type="email"
                        error={errors.identifier}
                    />
                )}
                <Select
                    label="Role"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value as ConfigurationUserRoleName)}
                    options={CONFIGURATION_ROLE_OPTIONS}
                    placeholder="Select role"
                    error={errors.role}
                />
                {!isSelf && (
                    <div className={styles.statusRow}>
                        <span className={styles.statusTitle}>Set Status Active</span>
                        <Toggle checked={flagActive} onChange={setFlagActive} />
                    </div>
                )}
            </div>
        </Popup>
    )
}

export default EditUserPopup
